/**
 * Persistent topbar — brand logo, primary nav (Today / Inventory /
 * Services), notifications bell, avatar with dropdown (theme toggle +
 * language toggle + sign out). Mirrors `.v2-topbar` in the mockup.
 *
 * Nav links use NavLink for active-route highlighting. The notifications,
 * avatar, and mobile burger menus are controlled dropdowns (useDropdown)
 * that close on outside-click + Escape — native <details> stayed open.
 *
 * Mobile (<md, 768px): pill nav + logo move into a burger dropdown on the
 * left; Walk-in collapses to an icon-only `+` button.
 * Desktop (≥md): logo + pill nav inline in the topbar, full labelled
 * Walk-in CTA. No JS breakpoint detection — Tailwind `md:` prefixes only.
 */
import { Bell, List, Moon, SignOut, Sun, User, X } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, NavLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { authApi } from '@/apis/workshopApis'
import { useSession } from '@/auth/SessionContext'
import { nameInitials } from '@vollgas/shared/ui'
import { useSignOut } from '@vollgas/shared/hooks'
import {
  useDismissAllNotifications,
  useDismissNotification,
  useNotifications,
} from '@/hooks/useStaff'
import { useI18n } from '@/i18n/TranslationContext'
import { useTheme, Theme } from '@/hooks/useTheme'
import { useDropdown } from '@/hooks/useDropdown'
import { useCurtainTransition } from '@/transitions/CurtainTransition'
import {
  BOARD_FILTERS,
  DEFAULT_FILTER,
  parseFilter,
  type BoardFilter,
} from '@/features/today/boardFilters'
import { cn } from '@vollgas/shared/utils'
import { type PresenceStatus } from '@vollgas/shared/hooks'

/** Popover enter/exit animation classes; reduced motion gets a plain fade. */
function popoverAnim(status: PresenceStatus): string {
  return status === 'closing'
    ? 'motion-safe:animate-popover-out motion-reduce:animate-fade-out'
    : 'motion-safe:animate-popover-in motion-reduce:animate-fade-in'
}

/**
 * TEMPORARY — withholds the Inventory and Services nav from the topbar.
 *
 * Their routes stay registered and reachable by URL; only the way in from
 * the bar is withheld, so restoring them is flipping this. The bar itself
 * is now the board's filters, and two catalog destinations sitting beside
 * five filters read as five-plus-two of the same thing when they are not.
 */
export const SHOW_CATALOG_NAV = false

// Pill nav item; active gets the accent fill + white text (glass selected-pill).
const NAV_ITEM_BASE = 'px-px13 py-px9 rounded-full text-13 font-semibold tracking-mk-p2 whitespace-nowrap text-glass-text-muted hover:text-glass-text transition-colors'
// Mobile burger-menu nav item — full-width, left-aligned, same active state.
const BURGER_ITEM = 'flex w-full items-center rounded-sm bg-transparent px-3 py-px11 text-left text-13 font-semibold text-glass-text-muted hover:bg-dash-hover hover:text-glass-text'

// `.dropdown-item` — 8px radius, 11px vertical / 12px horizontal padding.
const DROPDOWN_ITEM = 'flex w-full items-center gap-3 rounded-sm bg-transparent px-3 py-px11 text-left text-13 font-medium text-glass-text hover:bg-dash-hover'

/**
 * Notifications bell — a click-to-open popover listing the staff inbox
 * (NOTIFICATION.READ). The unread dot shows only when there are unread
 * entries.
 *
 * Dismissable per row and in bulk. Only rows ASSIGNED to this technician
 * can be dismissed: the bell deliberately also lists unassigned shop-wide
 * signals (low stock, reopened work orders), and letting one technician
 * clear those would hide them from everyone else. Those rows render
 * without an X rather than with one that 403s.
 */
function NotificationsBell() {
  const { t } = useI18n()
  const { data } = useNotifications()
  const items = data ?? []
  const hasUnread = items.some((n) => !n.readAt)
  const menu = useDropdown<HTMLDivElement>()
  const dismiss = useDismissNotification()
  const dismissAll = useDismissAllNotifications()
  const ownCount = items.filter((n) => n.assignedToUserId != null).length

  return (
    // `relative` is load-bearing: without it the popover anchors to the
    // sticky header (nearest positioned ancestor) and breaks its placement.
    <div ref={menu.ref} className="relative">
      <button
        type="button"
        aria-label={t.common.notifications}
        aria-haspopup="dialog"
        aria-expanded={menu.open}
        onClick={menu.toggle}
        className="relative grid h-9 w-9 cursor-pointer place-items-center rounded-full text-glass-text hover:bg-dash-hover"
      >
        <Bell size={18} />
        {/* Red, not the brand — an unread alert is somebody having to
            act. `bg-red` and not `bg-critical`: this app keeps its own
            warm palette and never adopted the glass system's critical
            token, so that class would compile to nothing and the dot
            would lose its colour altogether. */}
        {hasUnread && <span className="absolute right-2 top-2 h-2 w-2 rounded-full border-2 border-glass-base bg-red" />}
      </button>
      {menu.mounted && (
        <div
          data-state={menu.status}
          // `whitespace-normal` overrides the header's nowrap so bodies wrap;
          // the viewport width cap keeps the panel on-screen on phones.
          className={cn(
            'absolute right-0 top-[calc(100%+8px)] z-[60] max-h-[60vh] w-72 max-w-[calc(100vw-80px)] origin-top-right overflow-auto overflow-x-hidden whitespace-normal rounded-md border border-glass-border bg-white p-1.5 shadow-glass dark:bg-bg-elevated',
            popoverAnim(menu.status),
          )}
        >
          <div className="flex items-center justify-between gap-2 px-3 pb-1.5 pt-1">
            <span className="text-11 font-bold uppercase tracking-mk-up12 text-glass-text-muted">
              {t.notifications.title}
            </span>
            {ownCount > 0 && (
              <button
                type="button"
                disabled={dismissAll.isPending}
                onClick={() => dismissAll.mutate()}
                className="cursor-pointer text-11 font-semibold text-accent hover:underline disabled:opacity-50 disabled:no-underline"
              >
                {t.notifications.dismissAll}
              </button>
            )}
          </div>
          <hr className="my-1 border-t border-glass-divider" />
          {items.length === 0 ? (
            <p className="px-3 py-4 text-center text-12-5 text-glass-text-muted">{t.notifications.empty}</p>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id} className="group flex items-start gap-2 rounded-sm px-3 py-2 hover:bg-dash-hover">
                  <div className="min-w-0 flex-1">
                    {n.title && <div className="text-12-5 font-semibold text-glass-text">{n.title}</div>}
                    {n.body && <div className="mt-0.5 text-11-5 text-glass-text-muted">{n.body}</div>}
                  </div>
                  {n.assignedToUserId != null && (
                    <button
                      type="button"
                      disabled={dismiss.isPending}
                      aria-label={t.notifications.dismiss}
                      title={t.notifications.dismiss}
                      onClick={() => dismiss.mutate(n.id)}
                      // Hover-reveal. hover:none pins it visible on touch —
                      // this dashboard is used on shop-floor tablets, where
                      // a hover-only control would simply never appear.
                      className="grid h-6 w-6 shrink-0 cursor-pointer place-items-center rounded-full text-glass-text-muted opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 hover:bg-glass-fill hover:text-glass-text disabled:opacity-40 [@media(hover:none)]:opacity-100"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export function Topbar() {
  const { t, lang, setLang } = useI18n()
  const { theme, toggle } = useTheme()
  const { runWithCurtain } = useCurtainTransition()
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  // Only the board itself has a filter; from a job page no tab is active
  // until one is pressed, which is why this reads the path too.
  const activeFilter: BoardFilter | null =
    location.pathname === '/' ? parseFilter(params.get('filter')) : null

  /**
   * Select a slice — and go to the board if we are not on it.
   *
   * The default is written as a BARE path rather than `?filter=board`:
   * the resting state of the screen should have a clean URL, and a
   * missing param already parses to the default.
   */
  const goToFilter = (f: BoardFilter) => {
    navigate(f === DEFAULT_FILTER ? '/' : `/?filter=${f}`)
  }
  const qc = useQueryClient()
  const { user, setUser } = useSession()
  /**
   * Whoever is signed in, not a fixture.
   *
   * The avatar was the literal string "MK" — every technician in the shop
   * wore the same two letters, which is worse than no avatar at all on a
   * device that gets passed between people.
   *
   * Then it wore ONE letter. firstName and lastName are null on every
   * seeded staff row — the whole name lives in `displayName` — so the pair
   * joined to nothing, the email fallback fired, and `tech@softmaze.at`
   * initialled as "T". Same for the manager and the admin: a shared device
   * showing a single letter that is really the first letter of an address.
   *
   * So `displayName` fills whichever part the columns do not supply. It is
   * the backend's resolved name (User#resolvedDisplayName) and never empty.
   * The raw columns still win where they exist — a user who set a real
   * first and last name gets those, not a re-split of the label. Email
   * remains the last resort, because both parts are genuinely nullable: an
   * OAuth user can arrive with neither.
   */
  const initials = (() => {
    const [labelFirst = '', ...labelRest] = (user?.displayName?.trim() ?? '').split(/\s+/)
    const first = user?.firstName?.trim() || labelFirst
    // `.at(-1)`, not `[1]`: "Anna Maria Huber" should initial as AH, and a
    // middle name must not become the surname.
    const last = user?.lastName?.trim() || labelRest.at(-1) || ''
    const full = `${first} ${last}`.trim()
    if (full) return nameInitials(full)
    const local = user?.email?.split('@')[0]?.trim()
    return local ? nameInitials(local.replace(/[._-]+/g, ' ')) : '—'
  })()
  const burger = useDropdown<HTMLDivElement>()
  const menu = useDropdown<HTMLDivElement>()

  // The cached queries and the local user are dropped only once the server
  // confirms the session is gone. The previous version swallowed the
  // failure and cleaned up regardless, which is how a 403'd logout came to
  // look successful while the session stayed live.
  const {
    signOut: handleSignOut,
    error: signOutError,
    busy: signOutBusy,
  } = useSignOut({
    onSignedOut: () => {
      setUser(null)
      qc.clear()
      navigate('/login', { replace: true })
    },
    // Plain sign-out only; "everywhere else" lives on the profile screen.
    messages: { failed: t.common.signOutFailed },
  })

  return (
    <header className="glass-chrome sticky top-0 z-50 flex flex-shrink-0 items-center gap-px10 whitespace-nowrap border-b border-glass-border bg-glass-fill-strong px-6 py-px14 text-glass-text">
      {/* Mobile burger — visible only on <md. Houses logo (brand header) +
          nav links so the topbar itself stays uncluttered on narrow screens. */}
      <div ref={burger.ref} className="relative md:hidden">
        <button
          type="button"
          aria-label={t.common.menu}
          aria-haspopup="menu"
          aria-expanded={burger.open}
          onClick={burger.toggle}
          className="grid h-9 w-9 cursor-pointer place-items-center rounded-full text-glass-text hover:bg-dash-hover"
        >
          <List size={20} weight="bold" />
        </button>
        {burger.mounted && (
          <nav
            role="navigation"
            aria-label={t.common.menu}
            data-state={burger.status}
            className={cn(
              'absolute left-0 top-[calc(100%+10px)] z-[60] min-w-[220px] origin-top-left rounded-md border border-glass-border bg-white p-1.5 shadow-glass dark:bg-bg-elevated',
              popoverAnim(burger.status),
            )}
          >
            {/* Brand header inside the dropdown — mockup logo moves here on
                mobile. Not a link: the "Today" nav item sits right below it. */}
            <div className="flex items-center px-3 pb-2 pt-1">
              <img src="/Red-Just-Logo.png" alt="Vollgas" className="block h-[24px] w-auto" width={52} height={24} />
            </div>
            <hr className="my-1 border-t border-glass-divider" />
            {/* Filters, not nav — the burger holds the same slices the
                desktop bar does, so a phone is not left with a board it
                cannot filter. Inventory and Services are withheld from
                both; see SHOW_CATALOG_NAV. */}
            {BOARD_FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => {
                  goToFilter(f)
                  burger.close()
                }}
                aria-current={activeFilter === f ? 'true' : undefined}
                className={cn(BURGER_ITEM, activeFilter === f && '!text-glass-text')}
              >
                {t.filters[f]}
              </button>
            ))}
            {SHOW_CATALOG_NAV && (
              <>
                <NavLink to="/inventory" onClick={burger.close} className={({ isActive }) => cn(BURGER_ITEM, isActive && '!text-glass-text')}>
                  {t.nav.inventory}
                </NavLink>
                <NavLink to="/services" onClick={burger.close} className={({ isActive }) => cn(BURGER_ITEM, isActive && '!text-glass-text')}>
                  {t.nav.services}
                </NavLink>
              </>
            )}
          </nav>
        )}
      </div>

      {/* Desktop logo + pill nav — hidden on mobile (lives in the burger instead).
          The logo is a home link → Today view (T1). */}
      <Link to="/" aria-label={t.nav.today} className="hidden flex-shrink-0 md:block">
        <img src="/Red-Just-Logo.png" alt="Vollgas" className="h-[30px] w-auto" width={65} height={30} />
      </Link>

      {/* The board's filters, where the nav pills used to be. Buttons
          rather than NavLinks: these select a slice of one screen, and a
          route would make "Done" a place you can be rather than a way of
          looking. They DO navigate home first — pressed from a job page,
          a filter has to take you back to the board it filters. */}
      <div
        role="group"
        aria-label={t.filters.label}
        className="ml-1.5 hidden flex-shrink-0 gap-0.5 md:flex"
      >
        {BOARD_FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => goToFilter(f)}
            aria-current={activeFilter === f ? 'true' : undefined}
            className={cn(NAV_ITEM_BASE, activeFilter === f && '!bg-accent !text-white')}
          >
            {t.filters[f]}
          </button>
        ))}
      </div>
      {SHOW_CATALOG_NAV && (
        <nav className="ml-1.5 hidden flex-shrink-0 gap-0.5 md:flex">
          <NavLink to="/inventory" className={({ isActive }) => cn(NAV_ITEM_BASE, isActive && '!bg-accent !text-white')}>
            {t.nav.inventory}
          </NavLink>
          <NavLink to="/services" className={({ isActive }) => cn(NAV_ITEM_BASE, isActive && '!bg-accent !text-white')}>
            {t.nav.services}
          </NavLink>
        </nav>
      )}

      <div className="ml-auto flex flex-shrink-0 items-center gap-1.5">
        {/* No create actions up here: managers own intake (walk-ins +
            bookings land as jobs via the management app) — the tech
            works the task list. */}
        <NotificationsBell />

        {/* `relative` anchors the avatar menu here, not the sticky header. */}
        <div ref={menu.ref} className="relative">
          <button
            type="button"
            aria-label={t.common.profile}
            aria-haspopup="menu"
            aria-expanded={menu.open}
            onClick={menu.toggle}
            className="flex cursor-pointer items-center gap-1 p-0"
          >
            <span className="grid h-9 w-9 place-items-center rounded-full bg-accent text-13 font-bold text-white">
              {initials}
            </span>
          </button>
          {menu.mounted && (
            <div
              role="menu"
              data-state={menu.status}
              className={cn(
                'absolute right-0 top-[calc(100%+8px)] z-[60] w-72 max-w-[calc(100vw-40px)] origin-top-right rounded-md border border-glass-border bg-white p-1.5 shadow-glass dark:bg-bg-elevated',
                popoverAnim(menu.status),
              )}
            >
              <button type="button" onClick={() => { menu.close(); navigate('/profile') }} className={DROPDOWN_ITEM}>
                <User size={16} className="text-glass-text-muted" />
                {t.common.profile}
              </button>
              {/* Labels wrapped in a span so the conditional icon swap always
                  has a stable element sibling (a bare text node here can be
                  rewritten by a translation extension and break React). */}
              <button type="button" onClick={() => runWithCurtain(() => toggle())} className={DROPDOWN_ITEM}>
                {theme === Theme.Dark ? <Sun size={16} className="text-glass-text-muted" /> : <Moon size={16} className="text-glass-text-muted" />}
                <span>{theme === Theme.Dark ? t.common.switchToLight : t.common.switchToDark}</span>
              </button>
              <button
                type="button"
                onClick={() => runWithCurtain(() => setLang(lang === 'de' ? 'en' : 'de'))}
                className={DROPDOWN_ITEM}
              >
                <span className="grid h-4 w-4 place-items-center text-11 font-extrabold text-glass-text-muted">{lang === 'de' ? 'DE' : 'EN'}</span>
                <span>{lang === 'de' ? 'Deutsch' : 'English'}</span>
              </button>
              <hr className="my-1 border-t border-glass-divider" />
              <button
                type="button"
                disabled={signOutBusy}
                onClick={() => { menu.close(); void handleSignOut() }}
                className={DROPDOWN_ITEM}
              >
                <SignOut size={16} className="text-glass-text-muted" />
                {t.common.signOut}
              </button>
            </div>
          )}
        </div>
      </div>
      {signOutError ? (
        <div
          role="status"
          className="absolute inset-x-0 top-full bg-red-600 px-4 py-2 text-center text-13 font-semibold text-white"
        >
          {signOutError}
        </div>
      ) : null}
    </header>
  )
}
